import * as React from 'react';
import './styles.scss';
import { VotingButtonsOrResults } from 'client/respondent/voting/poll-set/containers/question-set-loaded';
import {
  VotingProps,
  NotLoggedInActions,
  NotLoggedInActionStatus,
} from 'client/respondent/core/types';
import {
  Btn,
  ButtonTypes,
  ClickableDiv,
  MaterialIcon,
  MaterialIconName,
} from 'client/shared/components/base';
import { VoteResultType } from 'client/shared/core/types';
import { RespondentActions } from 'client/respondent/core/reducers/actions';
import { useRespondentDispatch } from 'client/respondent/core/reducers/context';
import { ClientUrlUtils } from 'client/shared/core/helpers';
import { _dangerousUseEffectOnMount } from 'client/shared/helpers/hooks';
import {
  checkIfInProcessVoteIsValid,
  processPreLoginAction,
} from 'client/respondent/shared/functions';

const baseClass = 'pn-embed-question';

const copy = {
  submit: 'Submit',
};

export interface EmbedQuestionProps
  extends Omit<VotingProps.QuestionSetLoaded, 'events'> {
  readonly events: VotingProps.QuestionSetLoaded['events'] & {
    readonly onWholeQuestionClick: (() => void) | undefined;
  };
}

export const EmbedQuestion: React.FC<EmbedQuestionProps> = (props) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const dispatch = useRespondentDispatch();

  const voted = !!props.typedData.previousVote;

  const voteForQuestion = async () => {
    const vote = await props.events.submitVote();
    if (vote.type === VoteResultType.SUCCESS) {
      await props.events.followPublisher();
    } else if (vote.type === VoteResultType.ERROR) {
      setError(vote.message);
    }
    return vote;
  };
  const handleSubmit = async () => {
    if (!props.respId && !props.allowGuestRespondents) {
      dispatch(
        RespondentActions.promptRegistration({
          actionType: NotLoggedInActions.SET_VOTE,
          redirectLink: ClientUrlUtils.embed.question.path({
            questionId: props.id,
            setIdOrSlug: props.questionSetId,
            pubSlug: props.publishingEntity.slug,
          }),
          data: {
            setId: props.questionSetId,
            questionId: props.id,
          },
          status: NotLoggedInActionStatus.PRE_REGISTRATION,
          registrationType: null,
        })
      );
    } else {
      await voteForQuestion();
    }
  };
  _dangerousUseEffectOnMount(() =>
    processPreLoginAction(
      props.preLoginAction,
      NotLoggedInActions.SET_VOTE,
      dispatch,
      (act) => {
        if (act.data.questionId !== props.id) return null;
        return handleSubmit();
      }
    )
  );
  return (
    <MaybeClickableDiv action={props.events.onWholeQuestionClick}>
      <div
        className={`${baseClass}-title font-weight-bold font-size-large py-4 px-3`}
      >
        {props.title}
      </div>
      <div className="px-3">
        <VotingButtonsOrResults {...props} submitting={submitting} />
      </div>
      <div className="m-3 d-flex flex-row">
        <div>{error}</div>
        <div className="flex-grow-1" />
        {!voted && (
          <Btn
            action={async () => {
              setSubmitting(true);
              await handleSubmit();
            }}
            className="pn-respondent-voting-button-secondary p-2"
            disabled={
              !checkIfInProcessVoteIsValid(props.typedData, props.inProcessComment)
            }
            type={ButtonTypes.PRIMARY}
          >
            {copy.submit}
            <MaterialIcon className="pl-2" icon={MaterialIconName.CHECK} />
          </Btn>
        )}
      </div>
    </MaybeClickableDiv>
  );
};
EmbedQuestion.displayName = 'EmbedQuestion';

const MaybeClickableDiv: React.FC<{
  readonly action: (() => void) | undefined;
}> = (p) => {
  if (p.action) {
    return <ClickableDiv action={p.action}>{p.children}</ClickableDiv>;
  } else {
    return <div>{p.children}</div>;
  }
};
